import { canonicalStringify } from '@apollo/client/utilities';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { initialPagedGridInput } from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import { useRouteQueryRead } from 'src/routing/useRouteQueryRead';
import { useRouteLoaderQueryRef } from 'src/routing/useRouteQueryRef';
import type { CommodityCatalogRootQuery } from 'types/graphql';
import type { CatalogScope, CatalogTransport } from './catalogDataSource';
import { COMMODITY_CATALOG_ROOT_QUERY, catalogRootPage, catalogRootVariables } from './hooks/useCatalogTransport';

export const CATALOG_STORAGE_KEY = 'commodities_v3';
/** Commodity headers per root page — the root is paged by COMMODITY, never by product. */
export const COMMODITY_PAGE_SIZE = 100;

type RootSeed = {
  readonly variablesKey: string;
  readonly page: Promise<CommodityCatalogRootQuery>;
  readonly resolve: (data: CommodityCatalogRootQuery) => void;
  readonly reject: (error: unknown) => void;
  claimed: boolean;
};

function createRootSeed(variablesKey: string): RootSeed {
  let resolve: RootSeed['resolve'] = () => {};
  let reject: RootSeed['reject'] = () => {};
  const page = new Promise<CommodityCatalogRootQuery>((onData, onError) => {
    resolve = onData;
    reject = onError;
  });
  // An unclaimed seed (the scope moved before the first window was asked for) fails quietly.
  page.catch(() => {});
  return { variablesKey, page, resolve, reject, claimed: false };
}

/** The catalog's first commodity window, as the grid's persisted state will ask for it. */
export function preloadCatalogRoot(context: AppRouterContext) {
  const { gridFilters, quickFilters } = initialPagedGridInput(CATALOG_STORAGE_KEY);
  const scope: CatalogScope = { searchText: undefined, gridFilters, quickFilters };
  const variables = catalogRootVariables({ first: COMMODITY_PAGE_SIZE, scope });
  return preloadRouteQuery(context, COMMODITY_CATALOG_ROOT_QUERY, variables, { fetchPolicy: 'network-only' });
}

/**
 * Hands the route loader's first commodity window to the data source: the first root `getRows` for
 * exactly the preloaded variables resolves from the ref (read with useReadQuery by `reader`) instead of
 * asking again. Every later window, scope and refresh goes through `transport` unchanged.
 */
export function useCatalogRootSeed<TTransport extends CatalogTransport>(
  transport: TTransport,
  scope: CatalogScope
): { transport: TTransport; reader: ReactElement | null } {
  const variables = catalogRootVariables({ first: COMMODITY_PAGE_SIZE, scope });
  const loaderRef = useRouteLoaderQueryRef(COMMODITY_CATALOG_ROOT_QUERY, variables);
  const [first] = useState(() =>
    loaderRef ? { ref: loaderRef, seed: createRootSeed(canonicalStringify(variables)) } : undefined
  );
  const { read, reader } = useRouteQueryRead(first?.ref);

  useEffect(() => {
    if (read.data !== undefined) first?.seed.resolve(read.data);
    else if (read.error !== undefined) first?.seed.reject(read.error);
  }, [first, read]);

  const seeded = useMemo(() => {
    if (first === undefined) return transport;
    const { seed } = first;
    const fetchCommodityPage: CatalogTransport['fetchCommodityPage'] = (args) => {
      if (seed.claimed || canonicalStringify(catalogRootVariables(args)) !== seed.variablesKey) {
        return transport.fetchCommodityPage(args);
      }
      seed.claimed = true;
      return seed.page.then(catalogRootPage);
    };
    return { ...transport, fetchCommodityPage };
  }, [first, transport]);

  return { transport: seeded, reader };
}
